/**
 * Professional Collapsible Sidebar Navigation
 * Matches approved mockup design with smooth animations and active states
 * 
 * Features:
 * - 240px width collapsible to 64px with smooth transitions
 * - Three navigation sections: Main, Data Management, Analysis
 * - Active state with left border accent and background tint
 * - Professional hover effects and micro-interactions
 * - Mobile responsive with slide overlay
 */

import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, IconButton, useTheme, useMediaQuery, Drawer } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as ProjectsIcon,
  Assignment as ScenariosIcon,
  LocationOn as LocationsIcon,
  LocalShipping as VehiclesIcon,
  Work as JobsIcon,
  BarChart as ResultsIcon,
  TrendingUp as OptimizationIcon,
  Settings as SettingsIcon,
  ChevronLeft,
  ChevronRight,
  Inventory as ProductsIcon,
  AllInbox as ShipmentsIcon,
  Build as SkillsIcon,
  DirectionsCar as EnhancedVehiclesIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

// Navigation item interface
interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  section: 'main' | 'planning' | 'data' | 'advanced' | 'optimization' | 'analysis' | 'settings';
}

// Navigation section interface
interface NavigationSection {
  id: string;
  title: string;
  items: NavigationItem[];
}

// Component props interface
interface ProfessionalSidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  className?: string;
  // MPAD Mobile Enhancement: Add mobile-specific props
  mobileOpen?: boolean;
  onMobileToggle?: () => void;
}

// MPAD Enhanced Navigation - Workflow-Optimized Information Architecture
const navigationSections: NavigationSection[] = [
  {
    id: 'main',
    title: 'Core',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: <DashboardIcon />,
        path: '/dashboard',
        section: 'main'
      },
      {
        id: 'projects',
        label: 'Projects',
        icon: <ProjectsIcon />,
        path: '/projects',
        section: 'main'
      }
    ]
  },
  {
    id: 'planning',
    title: 'Planning & Setup',
    items: [
      {
        id: 'scenarios',
        label: 'Scenarios',
        icon: <ScenariosIcon />,
        path: '/scenarios',
        section: 'planning'
      },
      {
        id: 'data-management',
        label: 'Data Management',
        icon: <DashboardIcon />,
        path: '/data-management',
        section: 'planning'
      }
    ]
  },
  {
    id: 'data',
    title: 'Data Foundation',
    items: [
      {
        id: 'locations',
        label: 'Locations',
        icon: <LocationsIcon />,
        path: '/locations',
        section: 'data'
      },
      {
        id: 'vehicles',
        label: 'Vehicles',
        icon: <VehiclesIcon />,
        path: '/vehicles',
        section: 'data'
      },
      {
        id: 'enhanced-vehicles',
        label: 'Enhanced Vehicles',
        icon: <EnhancedVehiclesIcon />,
        path: '/enhanced-vehicles',
        section: 'data'
      },
      {
        id: 'jobs',
        label: 'Jobs',
        icon: <JobsIcon />,
        path: '/jobs',
        section: 'data'
      }
    ]
  },
  {
    id: 'advanced',
    title: 'MPAD Advanced Features',
    items: [
      {
        id: 'products',
        label: 'Products Catalog',
        icon: <ProductsIcon />,
        path: '/products',
        section: 'advanced'
      },
      {
        id: 'shipments',
        label: 'Shipments',
        icon: <ShipmentsIcon />,
        path: '/shipments',
        section: 'advanced'
      },
      {
        id: 'skills',
        label: 'Skills Management',
        icon: <SkillsIcon />,
        path: '/skills',
        section: 'advanced'
      }
    ]
  },
  {
    id: 'optimization',
    title: 'Optimization & Results',
    items: [
      {
        id: 'optimization',
        label: 'Run Optimization',
        icon: <OptimizationIcon />,
        path: '/optimization',
        section: 'optimization'
      },
      {
        id: 'optimization-results',
        label: 'Results',
        icon: <ResultsIcon />,
        path: '/optimization-results',
        section: 'optimization'
      }
    ]
  },
  {
    id: 'analysis',
    title: 'Analysis & Visualization',
    items: [
      {
        id: 'vehicle-summary',
        label: 'Vehicle Summary',
        icon: <VehiclesIcon />,
        path: '/vehicle-summary',
        section: 'analysis'
      },
      {
        id: 'map',
        label: 'Map View',
        icon: <LocationsIcon />,
        path: '/map',
        section: 'analysis'
      },
      {
        id: 'schedule',
        label: 'Schedule',
        icon: <JobsIcon />,
        path: '/schedule',
        section: 'analysis'
      }
    ]
  },
  {
    id: 'settings',
    title: 'Settings',
    items: [
      {
        id: 'settings',
        label: 'Settings',
        icon: <SettingsIcon />,
        path: '/settings',
        section: 'settings'
      }
    ]
  }
];

// Define features that require project context
const PROJECT_REQUIRED_FEATURES = [
  '/scenarios',
  '/data-management',
  '/locations',
  '/vehicles',
  '/enhanced-vehicles',
  '/jobs',
  '/products',
  '/shipments',
  '/skills',
  '/optimization',
  '/optimization-results',
  '/vehicle-summary',
  '/map',
  '/schedule'
];

export const ProfessionalSidebar: React.FC<ProfessionalSidebarProps> = ({
  currentPage,
  onNavigate,
  className = '',
  // MPAD Mobile Enhancement: Destructure mobile props
  mobileOpen = false,
  onMobileToggle
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // Extract project info from URL
  const getProjectIdFromUrl = () => {
    const pathParts = location.pathname.split('/');
    if (pathParts[1] === 'projects' && pathParts[2]) {
      const id = parseInt(pathParts[2], 10);
      return !isNaN(id) ? id : null;
    }
    return null;
  };
  const currentProjectId = getProjectIdFromUrl();
  const currentProject = currentProjectId ? { id: currentProjectId, name: `Project ${currentProjectId}` } : null;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  
  // MPAD Hover Enhancement: Desktop hover state management with flicker prevention
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Calculate if sidebar should be expanded (hover or focus for accessibility)
  const isExpanded = !isMobile && (isHovered || isFocused);
  
  // Debounced hover handlers to prevent flickering
  const handleMouseEnter = () => {
    if (isMobile) return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(true);
  };
  
  const handleMouseLeave = () => {
    if (isMobile) return;
    // Increased delay to prevent flicker during transitions and section header expansion
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 300); // 300ms delay to allow for smooth transitions
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // MPAD Enhanced: Generate project-aware URLs with query parameter preservation
  const getProjectAwareUrl = (basePath: string) => {
    const projectId = currentProject?.id;
    // Global pages that don't need project context
    const globalPaths = ['/dashboard', '/projects', '/settings'];
    const requiresProject = PROJECT_REQUIRED_FEATURES.includes(basePath);
    
    // Get current URL search parameters to preserve scenario/dataset context
    const currentParams = new URLSearchParams(location.search);
    const queryString = currentParams.toString();
    const queryAppend = queryString ? `?${queryString}` : '';
    
    console.log('🔗 [ProfessionalSidebar] getProjectAwareUrl:', {
      basePath,
      projectId,
      currentProject: currentProject ? { id: currentProject.id, name: currentProject.name } : null,
      isGlobalPath: globalPaths.includes(basePath),
      requiresProject,
      currentParams: Object.fromEntries(currentParams.entries()),
      queryAppend
    });
    
    // If feature requires project but no project is selected, redirect to projects page
    if (requiresProject && !projectId) {
      console.log('⚠️ [ProfessionalSidebar] Feature requires project but none selected, redirecting to projects');
      return '/projects?message=select-project';
    }
    
    if (projectId && !globalPaths.includes(basePath)) {
      const projectAwareUrl = `/projects/${projectId}${basePath}${queryAppend}`;
      console.log('✅ [ProfessionalSidebar] Generated project-aware URL with parameters:', projectAwareUrl);
      return projectAwareUrl;
    }
    
    console.log('⚠️ [ProfessionalSidebar] Returning base path (no project or global path):', basePath);
    return basePath;
  };

  // Determine active item based on current path
  const isItemActive = (item: NavigationItem): boolean => {
    if (item.path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(item.path);
  };

  // CRISIS FIX: Handle navigation item click with project-aware routing
  const handleItemClick = (item: NavigationItem) => {
    console.log('🎯 [ProfessionalSidebar] Navigation clicked:', {
      itemId: item.id,
      itemPath: item.path,
      currentProject: currentProject ? { id: currentProject.id, name: currentProject.name } : null
    });
    
    const url = getProjectAwareUrl(item.path);
    console.log('🚀 [ProfessionalSidebar] Navigating to URL:', url);
    
    navigate(url);
    onNavigate(item.id);
    
    // MPAD Mobile Enhancement: Close mobile drawer after navigation
    if (isMobile && onMobileToggle) {
      onMobileToggle();
    }
  };

  // Render navigation section
  const renderNavigationSection = (section: NavigationSection, index: number) => (
    <Box key={section.id} sx={{ mb: index < navigationSections.length - 1 ? 3 : 0 }}>
      {/* Section title - always rendered to prevent layout shifts */}
      <Typography
        variant="caption"
        id={`section-${section.id}`}
        sx={{
          px: 3,
          py: 1,
          display: 'block',
          color: 'var(--text-tertiary)',
          fontWeight: 'var(--weight-semibold)',
          fontSize: 'var(--text-xs)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          opacity: isExpanded ? 1 : 0,
          height: isExpanded ? 'auto' : 0,
          overflow: 'hidden',
          transition: 'opacity 0.25s cubic-bezier(0.25, 1, 0.5, 1), height 0.25s cubic-bezier(0.25, 1, 0.5, 1)',
          transitionDelay: isExpanded ? '100ms' : '0ms',
          pointerEvents: isExpanded ? 'auto' : 'none'
        }}
      >
        {section.title}
      </Typography>

      {/* Navigation items */}
      <List sx={{ py: 0 }} role="menu" aria-labelledby={`section-${section.id}`}>
        {section.items.map((item) => {
          const isActive = isItemActive(item);
          
          return (
            <ListItem
              key={item.id}
              component="button"
              onClick={() => handleItemClick(item)}
              role="menuitem"
              aria-current={isActive ? 'page' : undefined}
              aria-label={`Navigate to ${item.label}`}
              tabIndex={0}
              onFocus={() => !isMobile && setIsFocused(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleItemClick(item);
                }
              }}
              sx={{
                px: isExpanded ? 2 : 1,
                py: 1,
                cursor: 'pointer',
                borderRadius: 0,
                position: 'relative',
                transition: 'all 0.25s cubic-bezier(0.25, 1, 0.5, 1)',
                border: 'none',
                width: '100%',
                textAlign: 'left',
                background: 'none',
                
                // Focus styling for accessibility
                '&:focus-visible': {
                  outline: '2px solid var(--vrp-primary)',
                  outlineOffset: '-2px',
                  backgroundColor: 'var(--surface-tertiary)'
                },
                
                // Active state styling (Exact Mockup Match) - Fixed border positioning
                ...(isActive && {
                  background: 'color-mix(in srgb, var(--vrp-primary) 15%, transparent)',
                  color: 'var(--vrp-primary)',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '3px',
                    backgroundColor: 'var(--vrp-primary)',
                    zIndex: 1,
                  }
                }),
                
                // Hover state styling
                '&:hover': {
                  backgroundColor: isActive 
                    ? 'color-mix(in srgb, var(--vrp-primary) 20%, transparent)' 
                    : 'var(--surface-tertiary)',
                  
                  '& .MuiListItemIcon-root': {
                    color: isActive ? 'var(--vrp-primary)' : 'var(--text-primary)'
                  },
                  
                  '& .MuiListItemText-root': {
                    color: isActive ? 'var(--vrp-primary)' : 'var(--text-primary)'
                  }
                }
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: isExpanded ? 40 : 'auto',
                  justifyContent: 'center',
                  color: isActive ? 'var(--vrp-primary)' : 'var(--text-secondary)',
                  transition: 'color 0.25s cubic-bezier(0.25, 1, 0.5, 1)',
                  
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.25rem'
                  }
                }}
              >
                {item.icon}
              </ListItemIcon>
              
              {isExpanded && (
                <ListItemText
                  primary={item.label}
                  sx={{
                    ml: 1,
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.25s cubic-bezier(0.25, 1, 0.5, 1)',
                    transitionDelay: isExpanded ? '100ms' : '0ms',
                    
                    '& .MuiListItemText-primary': {
                      fontSize: 'var(--text-sm)',
                      fontWeight: isActive ? 'var(--weight-semibold)' : 'var(--weight-medium)',
                      color: isActive ? 'var(--vrp-primary)' : 'var(--text-secondary)',
                      transition: 'color 0.25s cubic-bezier(0.25, 1, 0.5, 1)',
                      whiteSpace: 'nowrap'
                    }
                  }}
                />
              )}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  // MPAD Hover Enhancement: Create sidebar content component with stable hover zone
  const sidebarContent = (
    <Box 
      id="main-navigation" 
      sx={{ 
        py: 3, 
        height: '100%',
        position: 'relative',
        // Add padding to create stable hover zone
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: -10, // Extend hover area to the left
          right: isExpanded ? 0 : -20, // Extend hover area to the right when collapsed
          bottom: 0,
          pointerEvents: 'auto',
          zIndex: -1
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={() => !isMobile && setIsFocused(true)}
      onBlur={(e) => {
        // Only remove focus if not focusing another element within sidebar
        if (!isMobile && !e.currentTarget.contains(e.relatedTarget as Node)) {
          setIsFocused(false);
        }
      }}
    >
      {navigationSections.map((section, index) => 
        renderNavigationSection(section, index)
      )}
    </Box>
  );

  // MPAD Mobile Enhancement: Return responsive drawer system
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 240,
            backgroundColor: 'var(--surface-secondary)',
            borderRight: '1px solid var(--border-subtle)',
            top: 48, // Below top ribbon
            height: 'calc(100vh - 48px)',
            overflowX: 'hidden',
            overflowY: 'auto',
            
            // Custom scrollbar styling
            '&::-webkit-scrollbar': {
              width: 4
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'var(--border-subtle)',
              borderRadius: 'var(--radius-sm)'
            }
          }
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // MPAD Hover Enhancement: Desktop view with hover-based expansion
  return (
    <Box
      component="nav"
      className={className}
      role="navigation"
      aria-label="Main navigation"
      aria-expanded={isExpanded}
      sx={{
        position: 'fixed',
        left: 0,
        top: 48, // Below top ribbon
        height: 'calc(100vh - 48px)',
        width: isExpanded || isTablet ? 240 : 64, // Hover to expand, tablet stays collapsed
        backgroundColor: 'var(--surface-secondary)',
        borderRight: isExpanded || isTablet ? '1px solid var(--border-subtle)' : 'none',
        transition: 'width 0.25s cubic-bezier(0.25, 1, 0.5, 1), border-right 0.25s cubic-bezier(0.25, 1, 0.5, 1)',
        zIndex: 100,
        overflowX: 'hidden',
        overflowY: 'auto',
        
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: 4
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'var(--border-subtle)',
          borderRadius: 'var(--radius-sm)'
        },
        
        // MPAD Hover Enhancement: Add subtle shadow when expanded
        boxShadow: isExpanded ? '0 4px 12px rgba(0, 0, 0, 0.05)' : 'none',
        
        // MPAD Hover Enhancement: Extended hover detection area to prevent flicker
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: isExpanded ? '100%' : 'calc(100% + 30px)', // Larger extended hover area when collapsed
          height: '100%',
          pointerEvents: 'auto',
          transition: 'width 0.25s cubic-bezier(0.25, 1, 0.5, 1)',
          zIndex: -1 // Behind content to avoid blocking interactions
        }
      }}
    >
      {sidebarContent}
    </Box>
  );
};

export default ProfessionalSidebar;

// Export types for use in other components
export type { ProfessionalSidebarProps, NavigationItem, NavigationSection };